import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { PaginateQuery } from '../core';
import { OperationEnum } from '../shared/enums/operation-type.enum';
import { ApiService } from './api.service';
import { normalizeTodayTasksCountPayload } from './customer-task-today-count.utils';
import { CustomerTaskApiDto, CustomerTaskListResponse } from '../core/models';

/**
 * Maps to `CustomerTask` API (`api/CustomerTask`).
 * `GET .../today` returns pending tasks for UTC today from the sync function (same payload as the timer).
 * `GET .../today/count` returns the total count (same filters as timer / list).
 */
export { normalizeTodayTasksCountPayload };

@Injectable({ providedIn: 'root' })
export class CustomerTaskService extends ApiService {
  constructor(http: HttpClient) {
    super(http, 'CustomerTask');
  }

  private static readonly jsonAcceptHeaders = new HttpHeaders({
    Accept: 'application/json',
  });

  /**
   * @param bustCache When true, appends a one-off query param so browsers/proxies do not serve a stale `/today` payload after mark-read.
   */
  getTodayTasksFromSync<T = unknown>(bustCache = false): Observable<T | null> {
    const params = bustCache ? new HttpParams().set('_', String(Date.now())) : undefined;
    return this.http
      .get<T>(`${this.route()}/today`, {
        headers: CustomerTaskService.jsonAcceptHeaders,
        ...(params ? { params } : {}),
      })
      .pipe(catchError(() => of(null)));
  }

  getTodayTasksCountFromSync(): Observable<number | null> {
    return this.http
      .get<unknown>(`${this.route()}/today/count`, {
        headers: CustomerTaskService.jsonAcceptHeaders,
      })
      .pipe(
        map((payload) => normalizeTodayTasksCountPayload(payload)),
        catchError(() => of(null)),
      );
  }

  /**
   * Lists tasks for a customer via `POST .../paginate`.
   * Do not use `GET .../CustomerTask/{id}` — that path is reserved for single-task routes
   * (e.g. PATCH/DELETE by id) and may return 405 for GET.
   */
  listByCustomer(customerID: string): Observable<CustomerTaskListResponse> {
    const query: PaginateQuery = {
      skip: 0,
      take: 5000,
      filters: [
        {
          field: 'customerID',
          operation: OperationEnum.Equals,
          value: customerID,
          condition: 'AND',
        },
      ],
    };
    return this.paginate(query).pipe(
      map((r) => ({ list: (r.list ?? []) as CustomerTaskApiDto[], total: r.total })),
    );
  }

  /** Lists tasks for a quote via `POST .../paginate`. */
  listByQuote(quoteID: string): Observable<CustomerTaskListResponse> {
    const query: PaginateQuery = {
      skip: 0,
      take: 5000,
      filters: [
        {
          field: 'quoteID',
          operation: OperationEnum.Equals,
          value: quoteID,
          condition: 'AND',
        },
      ],
    };
    return this.paginate(query).pipe(
      map((r) => ({ list: (r.list ?? []) as CustomerTaskApiDto[], total: r.total })),
    );
  }
}
